import React from "react";
import {
  AppShell as MantineAppShell,
  AppShellProps,
  Group,
  Button,
  Stack,
  Burger,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import type { AppShellNavbarConfiguration } from "@mantine/core";
import { Link } from "@inertiajs/react";
import { Heading6 } from "../UI/Typography";
import {
  IconHome,
  IconHammer,
  IconMail,
  IconUser,
  IconCamera,
  IconFeather,
  IconMusic,
} from "@tabler/icons-react";

interface CustomAppShellProps extends Omit<AppShellProps, "navbar"> {
  navbar?: AppShellNavbarConfiguration;
  showNavbar?: boolean;
}

const HEADER_HEIGHT = 60;

/**
 * AppShell - Main layout chrome (fixed header + collapsible navbar).
 *
 * The site footer is rendered in-flow at the bottom of PageShell — NOT
 * as a fixed AppShell.Footer — so it doesn't eat screen space on every
 * viewport.
 */
export const AppShell: React.FC<CustomAppShellProps> = ({
  children,
  navbar,
  showNavbar = true,
  ...props
}) => {
  const theme = useMantineTheme();
  const other = theme.other as Record<string, string>;
  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure();
  const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(false);

  const defaultNavbarConfig: AppShellNavbarConfiguration = {
    width: { base: 300 },
    breakpoint: "sm",
    collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
  };

  const navLinks = [
    { href: "/", label: "Home", icon: <IconHome size={18} /> },
    { href: "/tech", label: "Tech", icon: <IconHammer size={18} /> },
    { href: "/dance", label: "Dance", icon: <IconMusic size={18} /> },
    { href: "/media", label: "Media", icon: <IconCamera size={18} /> },
    { href: "/think", label: "Think", icon: <IconFeather size={18} /> },
    { href: "/about", label: "About", icon: <IconUser size={18} /> },
    { href: "/contact", label: "Contact", icon: <IconMail size={18} /> },
  ];

  return (
    <MantineAppShell
      header={{ height: HEADER_HEIGHT }}
      navbar={showNavbar ? navbar || defaultNavbarConfig : undefined}
      padding={0}
      styles={{
        main: {
          backgroundColor: "transparent",
          paddingTop: HEADER_HEIGHT,
        },
        header: {
          backgroundColor: "rgba(18,18,18,0.65)",
          backdropFilter: "blur(20px) saturate(180%)",
          WebkitBackdropFilter: "blur(20px) saturate(180%)",
          borderBottom: "1px solid rgba(212,85,43,0.12)",
          boxShadow: "inset 0 -1px 0 rgba(212,85,43,0.06)",
        },
        navbar: {
          backgroundColor: "rgba(18,18,18,0.75)",
          backdropFilter: "blur(20px) saturate(180%)",
          WebkitBackdropFilter: "blur(20px) saturate(180%)",
          borderRight: "1px solid rgba(212,85,43,0.12)",
        },
      }}
      {...props}
    >
      {showNavbar && (
        <MantineAppShell.Header p="md">
          <Group h="100%" px="md">
            <Burger
              opened={mobileOpened}
              onClick={toggleMobile}
              hiddenFrom="sm"
              size="sm"
              color={other.textPrimary}
            />
            <Burger
              opened={desktopOpened}
              onClick={toggleDesktop}
              visibleFrom="sm"
              size="sm"
              color={other.textPrimary}
            />
            <Heading6 style={{ marginLeft: "auto" }}>Ogarocious</Heading6>
          </Group>
        </MantineAppShell.Header>
      )}
      {showNavbar && (
        <MantineAppShell.Navbar p="md">
          <MantineAppShell.Section grow>
            <Stack gap="md">
              {navLinks.map((link) => (
                <Link key={link.href} href={link.href} style={{ textDecoration: "none" }}>
                  <Button
                    variant="subtle"
                    fullWidth
                    justify="flex-start"
                    leftSection={link.icon}
                    styles={{
                      root: {
                        color: other.textPrimary,
                        "&:hover": {
                          backgroundColor: "rgba(212,85,43,0.1)",
                        },
                      },
                    }}
                  >
                    {link.label}
                  </Button>
                </Link>
              ))}
            </Stack>
          </MantineAppShell.Section>
        </MantineAppShell.Navbar>
      )}
      {children}
    </MantineAppShell>
  );
};

export default AppShell;
